/** @format */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	signal
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { CertonaService } from '@product-recommendations/data-access';
import {
	AnalyticsService,
	DeviceService,
	FeatureConfigurationService,
	KillswitchService,
	PromoteIQService,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';

import { CriteoService } from '@shared/data-access/product-recommendations';
import { AuthenticationService } from '@shared/authentication/data-access';
import { selectUserStoreInfo } from '@pdp/data-access/state';
import {
	featureConfigCustomBadgesSet,
	removeAllFilters,
	selectDisplayableProductListData,
	selectSelectedFilters,
	selectIsProductListPageValid,
	selectPIQProducts,
	selectProductListAvailability,
	selectProductList,
	selectProductListFacetVOs,
	selectProductListPromotion,
	selectProductListSEO,
	selectSelectedSort,
	selectSkeletonLoader,
	selectIsProductListPageLoading,
	refreshProductList,
	selectProductVOs,
	selectIsAntiClearance,
	selectPageSize,
	selectCompareBarProducts,
	selectIsCompareFeatureHidden,
	selectFeatureFlags,
	selectSelectedFacetsWithCount,
	productListContainerFeatureFlagsSet,
	selectCriteoProducts
} from '@plp/data-access/state';
import {
	FacetVO,
	FacetAccordionElement,
	ProductVO,
	ProductList,
	PinnedContentSpot
} from '@plp/data-access/interfaces';
import { ProductListCompareService, ProductListMlService } from '@plp/data-access/services';
import { DisplayableProductListItem } from '@plp/data-access/types';
import { MLItem, MLItemType, MLStore } from '@machine-learning/data-access';
import { Store } from '@ngrx/store';
import { selectPageType } from '@state/data-access';
import { BreakPointNew, FeatureConfigurationKeys } from '@shared/data-access/enums';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { WishListService } from '@shared/favorites/data-access/services';
import { Subject, of } from 'rxjs';
import {
	concatMap,
	filter,
	map,
	shareReplay,
	skip,
	skipWhile,
	take,
	takeUntil,
	tap,
	withLatestFrom
} from 'rxjs/operators';
import { IMPORTS, PROVIDERS } from './product-list-constants';
import { ProductRecommendationsComponent } from '@product-recommendations/components/product-recommendations';
import { HomrTemplateComponent } from '@plp/homr/components/homr-template';
import {
	Chain,
	CompareBarProduct,
	CustomBadgeFeatureParams,
	Environment,
	ProductListSEO,
	PromoteIQ
} from '@shared/data-access/interfaces';
import { GoogleAdManagerComponent } from '@shared/components';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'plp-srlp-product-list-container',
	standalone: true,
	templateUrl: './product-list-container.component.html',
	styleUrl: './product-list-container.component.scss',
	imports: [
		...IMPORTS,
		HomrTemplateComponent,
		GoogleAdManagerComponent,
		ProductRecommendationsComponent,
		HmfButtonComponent
	],
	providers: PROVIDERS,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListContainerComponent implements OnInit, OnDestroy, AfterViewInit {
	/**** START OF FEATURE FLAGS PROPERTIES ************************/
	readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

	compareCheckboxEnabled = computed(() => this.featureFlags().compareCheckboxEnabled);
	enableTooltipSurvey = computed(() => this.featureFlags().enableTooltipSurvey);
	plpShowCustomerRating = computed(() => this.featureFlags().plpShowCustomerRating);
	sameDayDeliveryEnabled = computed(() => this.featureFlags().sameDayDeliveryEnabled);
	showNewSponsoredLabel = computed(() => this.featureFlags().showNewSponsoredLabel);
	sortPositionChangeEnabled = computed(() => this.featureFlags().sortPositionChangeEnabled);
	useImageSwatches = computed(() => this.featureFlags().useImageSwatches);

	private readonly UNSUBSCRIBE$: Subject<void> = new Subject();

	readonly availabilityList$ = this.store.select(selectProductListAvailability).pipe(
		tap((availabilityData) => {
			this.showAvailabilityData = this.featureFlags().availabilityOnProductCardsEnabled;
			if (Array.isArray(availabilityData) && availabilityData.length === 0) {
				this.showAvailabilityData = false;
			}
		}),
		takeUntil(this.UNSUBSCRIBE$)
	);
	readonly isCompareFeatureHidden$ = this.store
		.select(selectIsCompareFeatureHidden)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly compareList$ = this.store
		.select(selectCompareBarProducts)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly displayableProductListData$ = this.store.select(selectDisplayableProductListData).pipe(
		tap((displayableProducts) => {
			// This is used to show/hide the top and bottom pagination
			this.noProducts = displayableProducts && displayableProducts.length === 0;
		}),
		shareReplay(1),
		takeUntil(this.UNSUBSCRIBE$)
	);
	readonly isAntiClearance$ = this.store
		.select(selectIsAntiClearance)
		.pipe(shareReplay(1), takeUntil(this.UNSUBSCRIBE$));
	readonly isProductListPageLoading$ = this.store
		.select(selectIsProductListPageLoading)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly isProductListPageValid$ = this.store
		.select(selectIsProductListPageValid)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly pageType$ = this.store.select(selectPageType).pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly piqProducts$ = this.store.select(selectPIQProducts).pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly criteoProducts$ = this.store
		.select(selectCriteoProducts)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListData$ = this.store
		.select(selectProductList)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListFacetVOs$ = this.store.select(selectProductListFacetVOs).pipe(
		takeUntil(this.UNSUBSCRIBE$),
		map((facetVOs: FacetVO[]) => {
			if (!facetVOs || !facetVOs.length) return;

			facetVOs = facetVOs.filter((facetVO) => facetVO.valueList && facetVO.valueList.length);

			if (facetVOs.some((facet) => facet.attrIdentifier === 'facetCatgroup')) {
				this.hasCatBubbles = true;
			}
			this.openFacets = this.getOpenFacets(facetVOs);

			return facetVOs || [];
		})
	);
	readonly productListSEOData$ = this.store
		.select(selectProductListSEO)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productVOs$ = this.store.select(selectProductVOs).pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly promoList$ = this.store
		.select(selectProductListPromotion)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedFacetsWithCount$ = this.store
		.select(selectSelectedFacetsWithCount)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedSort$ = this.store
		.select(selectSelectedSort)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedStoreInfo$ = this.store.selectSignal(selectUserStoreInfo);
	readonly showSkeletonLoader$ = this.store
		.select(selectSkeletonLoader)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly pageSizeStore$ = this.store.select(selectPageSize).pipe(takeUntil(this.UNSUBSCRIBE$));

	productListData: ProductList | null = null;
	// This is used to track the referrer for QuickView, based on the page type
	productCardReferrer: 'plp' | 'srlp' = 'plp';
	productInfoList: string[] = [];
	chainAbbr = '';
	loaderDummyArray = [];
	filtersOpen = false;
	selectedFilterCount = 0;
	sortingByValidValues = true;
	itemsPerRow = 4;
	isFulfillmentFiltersSelected = false;
	isAuthenticated = false;
	nikeConnectedProducts: string[] = [];
	currentFacets: {
		facet: string;
		values: {
			count: number;
			value: string;
		}[];
	}[] = [];
	noProducts = false;
	showAvailabilityData = true;

	/*
		This is the list of the filters/facets tab in the left column
		it is used to mantain the status as the filter component goes throgh the whole lifecycle
		Example: { 'Brand': { open: true, showMore: false} }
		Means the Brand item in the accordion is open but with the complete list collapsed
	*/
	openFacets: Record<string, { open: boolean; showMore: boolean }> | null = null;
	dpld: any[] = [];
	readonly MAX_NUMBER_ITEMS_PER_ROW = 4;
	readonly DEFAULT_IMAGE_PAGE_POSITION = 0;
	readonly IMAGE_PAGE_POSITION_5 = 5;
	/**
	 * @description
	 * To display mobile view on screens less than 1024px.
	 */
	deviceBreakPoint: BreakPointNew;

	/**
	 * @description
	 * Check If the certona response have scheme for PLA
	 */
	isResonanceReady = false;
	// Certona scheme name
	scheme2 = 'family1_rr';
	// Response for Certona Recommendations
	certonaRecommendationsResponse: any;

	// Check If the certona response have scheme for cart
	isCartCertona = false;
	hasCatBubbles = false;
	showNoSearchResultsPage = false;
	pageType = 'plp';

	currentFilterIndex = signal<number | null>(null);

	protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

	/**** Start of ML Integration properties ************************/
	private intersectionParams: IntersectionObserverInit = {
		// This is added to account for the space taken up by the product-list-header.
		// If the user is scrolling up, the impression will wait to fire until
		// the last portion of the image is out from under the header.
		root: null,
		rootMargin: '0%',
		threshold: 1 // 0.0 - 1.0 = 0% - 100%
	};
	private productCardObserver!: IntersectionObserver;
	private ecodesAlreadyReported: string[] = [];

	resetTheObserver = (): void => {
		this.productCardObserver = new IntersectionObserver(
			this.handleProductCardIntersection.bind(this),
			this.intersectionParams
		);
	};
	/**** End of ML Integration Properties ************************/

	private productListSEOData: ProductListSEO | null = null;
	isStickySearchEnabled = false;
	isFilterContainerSticky = false;
	isPlp3Template = false;
	compareListEcodes: string[] = [];
	isCompareAnalyticsTriggered = false;
	skipLinkOpen = false;
	onViewGridItemBeacon = '';
	gridViewBeaconFired = false;
	productCardViewBeacons: string[] = [];

	constructor(
		private readonly store: Store,
		private readonly chain: Chain,
		private readonly productContainerService: CertonaService,
		private readonly changeRef: ChangeDetectorRef,
		private readonly killSwitchService: KillswitchService,
		private readonly utilityService: UtilityService,
		private readonly featureConfigurationService: FeatureConfigurationService,
		private readonly router: Router,
		private readonly deviceService: DeviceService,
		private readonly promoteIQService: PromoteIQService,
		private readonly plpMlService: ProductListMlService,
		private readonly plpCompareService: ProductListCompareService,
		private readonly authService: AuthenticationService,
		private readonly environment: Environment,
		private readonly wishlistService: WishListService,
		private readonly elementRef: ElementRef,
		private readonly analyticsService: AnalyticsService,
		private readonly criteoService: CriteoService,
		@Inject(WINDOW) private readonly window: Window,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {
		this.chainAbbr = chain.chainIdentifierAbbr;
		/*
		 * The callback is being bound to `this`
		 * in order to retain the context of the component
		 * within the function. Without it, the context of `this`
		 * changes to the IntersectionObserver class.
		 */
		if (isPlatformBrowser(this.platformId)) {
			this.productCardObserver = new IntersectionObserver(
				this.handleProductCardIntersection.bind(this),
				this.intersectionParams
			);
		}
	}

	ngOnInit(): void {
		this.featureConfigurationService.onFeature(
			FeatureConfigurationKeys.SET_PRODUCT_CUSTOM_BADGE,
			this.setCustomBadges.bind(this)
		);

		this.pageSizeStore$.subscribe(
			(pageSizeElement) => (this.loaderDummyArray = new Array(pageSizeElement))
		);
		this.pageType$.subscribe((pageType) => (this.pageType = pageType));
		this.getHomrBannerData();
		this.updateFilterCountAndFilterQueryParam();
		this.setUpSelectorSubscriptions();
		this.setupCertonaRecommendations();

		if (
			isPlatformBrowser(this.platformId) &&
			this.environment.thirdParty.auth0[this.chain.chainIdentifierAbbr].enabled
		) {
			this.getIsAuthenticated();
		}
	}

	ngAfterViewInit(): void {
		if (this.featureFlags().isPLPMLIntegrationEnabled && isPlatformBrowser(this.platformId)) {
			this.setItemsPerRow();
			/* This sets up the observers for the first page after all cards are fully loaded.
			 *  Otherwise, updates to the displayableProductListData calls this. */
			this.setupProductCardObservers();
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	redirectToPLPOrPDP(productListData): void {
		const landingPageURL = productListData.searchVO.landingPageURL;
		if (landingPageURL.toLowerCase().includes('www.')) {
			if (landingPageURL.toLowerCase().includes('http')) {
				window.location.assign(landingPageURL);
			} else {
				window.location.assign(`https://${landingPageURL}`);
			}
		} else if (!landingPageURL.startsWith('/')) {
			window.location.href = landingPageURL;
		} else {
			window.location.href = `${window.location.protocol}//${window.location.hostname}${landingPageURL}`;
		}
	}

	setUpSelectorSubscriptions(): void {
		this.productListData$.subscribe((productList: ProductList | null) => {
			if (!productList) {
				return;
			}
			// If the landingPageURL is present, redirect to the PLP, /search/ -> /f/
			if (productList.searchVO.landingPageURL !== undefined) {
				this.redirectToPLPOrPDP(productList);
				return;
			}

			this.addSponsoredIntersectionObservers();
			this.productListData = productList;
			this.productCardReferrer = productList.searchVO.isFamilyPage ? 'plp' : 'srlp';
			this.showNoSearchResultsPage =
				productList?.productVOs &&
				productList.productVOs.length === 0 &&
				this.productListSEOData === null &&
				this.selectedFilterCount === 0;

			if (productList.didYouMeanVO && productList.didYouMeanVO.suggestions.length) {
				this.router.navigate([], {
					queryParams: { searchTerm: productList.searchVO.searchTerm },
					queryParamsHandling: 'merge'
				});
			}
		});

		this.selectedSort$.subscribe((sortNum: { label: string; value: number }) => {
			this.sortingByValidValues = this.isSortNumberValid(sortNum.value);
		});

		this.selectedFacetsWithCount$.subscribe((facets) => {
			this.currentFacets = facets;
		});

		this.displayableProductListData$.subscribe((displayableProducts) => {
			this.dpld = displayableProducts;
		});

		this.compareList$.subscribe((compareProducts: CompareBarProduct[]) => {
			this.compareListEcodes = compareProducts.map((cp) => cp.ecode);
			this.changeRef.detectChanges();
		});
	}

	getHomrBannerData(): void {
		this.productListSEOData$.subscribe((response) => {
			this.productListSEOData = response;
			this.isPlp3Template = this.productListSEOData?.attr?.LAYOUT_TYPE?.value === '8';
		});
	}

	addSponsoredIntersectionObservers(): void {
		if (
			!isPlatformBrowser(this.platformId) ||
			!this.utilityService.hasIntersectionObserverSupport()
		) {
			return;
		}

		if (this.featureFlags().isUsingCriteoSPAAdServer) {
			this.criteoProducts$
				.pipe(
					filter((products) => products?.placements[1]?.inGrid[0]?.products?.length > 0)
				)
				.subscribe((products) => {
					//set timeout so cards render before we select them from the DOM
					setTimeout(() => {
						const allProducts = products?.placements[1]?.inGrid[0]?.products;
						this.onViewGridItemBeacon = products?.placements[1].inGrid[0].OnViewBeacon;
						for (let i = 0, length = allProducts.length; i < length; ++i) {
							const item = allProducts[i];
							const trackingId = `piq-${item.ProductId}`;
							const sponsoredProductCard = document.getElementsByClassName(
								trackingId
							)[0] as HTMLElement;
							const criteoCallback = () => {
								this.criteoService.fireBeacon(item.OnViewBeacon);
							};
							if (
								sponsoredProductCard &&
								!this.productCardViewBeacons.includes(item.OnViewBeacon)
							) {
								this.utilityService.singleViewIntersectionObserver(
									sponsoredProductCard,
									criteoCallback,
									0.5
								);
								this.productCardViewBeacons.push(item.OnViewBeacon);
							}
						}
						this.gridViewBeaconFired = false;
					}, 10);
				});
		} else {
			this.piqProducts$
				.pipe(filter((products) => products && products.length > 0))
				.subscribe((products) => {
					//set timeout so cards render before we select them from the DOM
					setTimeout(() => {
						// Intersection Observer for Promote IQ impression tracker
						// Promote IQ impression tracker needs to know when each individual product rec is in view
						for (let i = 0, length = products.length; i < length; ++i) {
							const item = products[i];
							const trackingId = `piq-${item.product.ID}`;
							const sponsoredProductCard = document.getElementsByClassName(
								trackingId
							)[0] as HTMLElement;
							const promoteIQCallback = () => {
								this.promoteIQService.fireTrackingEvent$(item.impression_tracker);
							};
							if (sponsoredProductCard) {
								this.utilityService.singleViewIntersectionObserver(
									sponsoredProductCard,
									promoteIQCallback,
									0.5
								);
							}
						}
					}, 10);
				});
		}
	}

	async removeFilters(): Promise<void> {
		await this.utilityService.yieldTask(() => {
			this.store.dispatch(removeAllFilters());
			this.store.dispatch(refreshProductList());
		});
	}

	isSortNumberValid(selectedSortNum: number): boolean {
		return !(selectedSortNum === 2 || selectedSortNum === 3 || selectedSortNum === 4);
	}

	setupProductCardObservers(): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		this.displayableProductListData$
			.pipe(
				skip(1),
				filter((dpld) => !!dpld?.length),
				withLatestFrom(this.productListData$, this.showSkeletonLoader$),
				skipWhile(
					([dpld, productList, listIsLoading]) =>
						listIsLoading || !dpld.length || !productList
				)
			)
			.subscribe(([dpld]) => {
				// Clear the list of ecodes that have been reported to the ML API
				this.ecodesAlreadyReported = [];
				this.productCardObserver.disconnect();
				this.resetTheObserver();
				const productVOs = dpld.filter(
					(product) => (product as ProductVO).parentPartnumber
				);
				this.setNikeConnectedProducts(productVOs);

				// This is used to track the ecodes of the products that are displayed on the page.
				// parentPartnumber -> regular products, product_id -> sponsored products
				this.productInfoList = dpld.map(
					(item: DisplayableProductListItem): string =>
						(item as ProductVO).parentPartnumber ||
						(item as PromoteIQ).product_id ||
						`${(item as PinnedContentSpot).csid}-${(item as PinnedContentSpot).pinnedPosition}`
				);

				this.changeRef.detectChanges();

				dpld.forEach((x, index) => {
					const pcimg: Element | null =
						this.elementRef.nativeElement.querySelector(
							`#plp-container #product-card-${index} .product-card-image`
						) ||
						this.elementRef.nativeElement.querySelector(
							`#plp-container #sponsored-product-${index} .product-card-image`
						) ||
						this.elementRef.nativeElement.querySelector(
							`#plp-container #pinned-content-${index} .pinned-content-intersection-observation-block`
						);

					if (pcimg) {
						this.productCardObserver.observe(pcimg);
					}
				});
			});
	}

	recordClickEvent(
		item: { event: PointerEvent; pdpUrl: string },
		product: ProductVO,
		index: number
	): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		// This is used here to prevent the product-card's <a> tag from navigating to the PDP before the Click Event is sent.
		// Navigation is handled below in the complete() callback.
		item.event.preventDefault();
		// catgroupId is only utilized on family pages, not search. If -1 is detected, an empty string is ultimately sent in the API payload.
		const catgroupId: number | null = this.productListSEOData?.catgroupId || null;
		this.plpMlService.sendClickEventData(
			MLStore[this.chainAbbr.toUpperCase()],
			product,
			this.dpld,
			this.productListData,
			0,
			catgroupId,
			index
		);
		this.window.location.href = item.pdpUrl;
	}

	handleCompareSelection(event: { compareData: CompareBarProduct; isChecked: boolean }): void {
		if (event.isChecked) {
			this.compareCheckboxUsedAnalytics();
			const compareSource = this.productListSEOData?.id
				? `PLP: ${this.productListSEOData.id}`
				: `Search: ${this.productListData?.searchVO?.searchTerm}`;
			event.compareData.compareSource = compareSource;
			event.compareData.gridImageURL = this.plpCompareService.modifyImgSrcWidthAndHeight(
				event.compareData.imgURL
			);

			this.plpCompareService.addProductToCompareList(event.compareData);
		} else {
			this.plpCompareService.removeProductFromCompareList(event.compareData.ecode);
		}
	}

	//To be removed once the compare feature goes to 100% in produciton.
	private compareCheckboxUsedAnalytics(): void {
		//Trigger analytics on compare checkbox click only once per page view
		if (!this.isCompareAnalyticsTriggered) {
			this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
				event: 'compareCheckboxUsed'
			});
			this.isCompareAnalyticsTriggered = true;
		}
	}

	@HostListener('window:popstate', ['$event'])
	refreshPageOnBack(): void {
		// When the user hits the back/forward button, we are not correctly identifying queryparam changes and we are not refreshing
		// This is a hacky fix until we can properly fix back/forward button queryParam change refresh
		window.location.reload();
	}

	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.styleStickyFilterContainer();
		this.setItemsPerRow(true);
		// TODO: explore the potential of adding debouncing to the HostListeners to reduce calls.
		// Or evaluate the need for HostListeners over a better alternative.
	}

	@HostListener('window:scroll', ['$event'])
	onScroll(): void {
		this.styleStickyFilterContainer();
	}

	@HostListener('document:WindowFlagsEvent', [])
	onWindowFlagsEvent() {
		// When this custom event is triggered, we need to update the feature flags
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((featureFlags: Record<string, boolean>) => {
				this.store.dispatch(
					productListContainerFeatureFlagsSet({ featureFlags: featureFlags })
				);
				this.changeRef.detectChanges();
			});
	}

	handleTabPressed() {
		this.skipLinkOpen = true;
	}

	private setCustomBadges(customBadgeParams: CustomBadgeFeatureParams): void {
		const customBadges = {};
		const { ecodes, text, textColor, background } = customBadgeParams;

		if (ecodes && text && textColor && background) {
			ecodes.forEach((ecode) => {
				customBadges[ecode] = {
					text,
					textColor,
					background
				};
			});

			this.store.dispatch(featureConfigCustomBadgesSet({ customBadges }));
		}
	}

	/**
	 * Styles the sticky filter container based on certain conditions.
	 */
	private styleStickyFilterContainer(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		this.isStickySearchEnabled = !!document.querySelector(
			'.dsg-header-search-sticky-enabled.sticky'
		);

		const stickyFilterContainer: Element | null =
			this.deviceBreakPoint <= 3
				? this.elementRef.nativeElement.querySelector('#sticky-filter-container-mobile')
				: this.elementRef.nativeElement.querySelector('#sticky-filter-container-desktop');

		// Edge Cases: Color Breakout Page, No Search Results - no filter is present
		if (!stickyFilterContainer) {
			return;
		}

		const stickyFilterBottom = stickyFilterContainer.getBoundingClientRect().bottom;

		const filterTop = parseInt(window.getComputedStyle(stickyFilterContainer).top);

		const compareBar =
			this.elementRef.nativeElement.querySelector('#compare-bar-mobile') ||
			this.elementRef.nativeElement.querySelector('#compare-bar-desktop');

		const fulfilmentHeightForMobile =
			this.deviceBreakPoint <= 3 && this.isFulfillmentFiltersSelected
				? this.elementRef.nativeElement.querySelector('.sticky-filter-chip').clientHeight
				: 0;

		if (stickyFilterContainer && compareBar) {
			const compareBarHeight = parseInt(window.getComputedStyle(compareBar).height);

			this.isFilterContainerSticky =
				stickyFilterBottom <=
				112 + compareBarHeight + filterTop + fulfilmentHeightForMobile;
		} else if (stickyFilterContainer && !compareBar) {
			this.isFilterContainerSticky =
				stickyFilterBottom <= 112 + filterTop + fulfilmentHeightForMobile;
		}
	}

	private getIsAuthenticated(): void {
		this.authService
			.getIsAuthenticated$()
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				concatMap((authResponse: boolean) => {
					this.isAuthenticated = authResponse;
					if (!this.isAuthenticated) {
						return of([]);
					}
					this.window.addEventListener('updateFavoritesStoreData', () => {
						this.updateFavoritesFromQuickview();
					});

					return this.wishlistService.getFavoritedEcodes();
				})
			)
			.subscribe();
	}

	private updateFavoritesFromQuickview() {
		this.wishlistService.getFavoritedEcodes().pipe(take(1)).subscribe();
	}

	private setupCertonaRecommendations(): void {
		// Check to see if a Certona callback has been defined already
		if (typeof this.window.certonaRecommendations === 'undefined') {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.certonaRecommendations = this.getCertonaRecommendations.bind(this);
		}

		// Check to see if a Certona callback has been defined already
		if (
			typeof this.window.DSGShoppingPathJS === 'undefined' ||
			typeof this.window.DSGShoppingPathJS.certonaRecommendations === 'undefined'
		) {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.DSGShoppingPathJS = {
				certonaRecommendations: this.getCertonaRecommendations.bind(this)
			};
		}
	}

	private getCertonaRecommendations(certonaResponse: any): void {
		// Check to see if there is a response from Certona
		if (typeof certonaResponse !== 'undefined') {
			if (typeof this.certonaRecommendationsResponse === 'undefined') {
				// Set the response on the component
				this.certonaRecommendationsResponse = certonaResponse;
			} else if (
				this.certonaRecommendationsResponse.resonance &&
				this.certonaRecommendationsResponse.resonance.schemes &&
				certonaResponse.resonance &&
				certonaResponse.resonance.schemes
			) {
				const mergedCertonaSchemes = [
					...this.certonaRecommendationsResponse.resonance.schemes,
					...certonaResponse.resonance.schemes
				];

				const uniqueCertonaSchemes = [];

				// Map for testing uniqueness
				const schemeMap = new Map();

				// Iterate the merged schemes
				for (const item of mergedCertonaSchemes) {
					// Check to see if the scheme exists already
					if (!schemeMap.has(item.scheme)) {
						// set any value to Map
						schemeMap.set(item.scheme, true);
						// Add the scheme to the result Array
						uniqueCertonaSchemes.push(Object.assign({}, item));
					}
				}

				// Set the response on the component by merging the new with the existing
				this.certonaRecommendationsResponse = {
					resonance: {
						schemes: uniqueCertonaSchemes
					}
				};
			}
		}
		this.changeRef.detectChanges();
		this.productContainerService.recommendations(this.certonaRecommendationsResponse);
	}

	/**
	 * @description
	 * When Selected Filters changes, update the filter count and update the query params
	 */
	private updateFilterCountAndFilterQueryParam(): void {
		this.selectedFilters$.subscribe((filters) => {
			let count = 0;
			// Loop through different selected filter types to retrieve the total count of filters selected
			Object.keys(filters).forEach((prop) => {
				count += filters[prop].length;
			});
			this.selectedFilterCount = count;

			// For page onload
			this.isFulfillmentFiltersSelected = !!filters['facetStore']?.length;
		});
	}

	/**
	 * Starting from the target element, recursively step up to the next parent element
	 * that contains the id number found in `product-card-`, `sponsored-product-`, or `pinned-content-`
	 * used to obtain the list of ecodes in each respective product row.
	 */
	private getProductCardMetadata(
		el: Element | HTMLElement | null
	): { index: number; isPinnedProduct: boolean; type: string } | undefined {
		const parentCardIdMatch: RegExpMatchArray | null = el?.id.match(
			/^(product-card-|sponsored-product-|pinned-content-)\d+$/
		);

		if (parentCardIdMatch && el && el.id) {
			// "product-card-5" => {index: 5, isPinnedProduct: false, type: "product-card"}
			const isPinnedProduct: boolean = el.getAttribute('data-em')?.includes('_pinned');
			const index = parseInt(el.id.split('-')[2], 10);
			const type = parentCardIdMatch[1].slice(0, -1); // use the regex match term and trim off the '-' at the end => "product-card"
			return { index, isPinnedProduct, type };
		} else if (el && el.parentElement) {
			return this.getProductCardMetadata(el.parentElement);
		} else {
			return undefined;
		}
	}

	private setItemsPerRow(forceCalculate = false): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		this.deviceBreakPoint = this.deviceService.getDeviceBreakPointNew(forceCalculate);

		switch (this.deviceBreakPoint) {
			case BreakPointNew.EXTRAEXTRASMALL:
				this.itemsPerRow = 1;
				break;
			case BreakPointNew.EXTRASMALL:
			case BreakPointNew.SMALL:
				this.itemsPerRow = 2;
				break;
			case BreakPointNew.MEDIUM:
			case BreakPointNew.LARGE:
			case BreakPointNew.EXTRALARGE:
				this.itemsPerRow = 3;
				break;
			case BreakPointNew.EXTRAEXTRALARGE:
				this.itemsPerRow = 4;
				break;
		}
	}

	// This is the callback used by the IntersectionObserver to handle when each new row of items enters the viewport.
	private handleProductCardIntersection(entries: IntersectionObserverEntry[]): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		const itemGroup: MLItem[] = [];
		const intersectingCards = entries.filter((entry) => entry.isIntersecting);
		if (!intersectingCards.length) {
			return;
		}

		for (const item of intersectingCards) {
			const card: { index: number; isPinnedProduct: boolean; type: string } | undefined =
				this.getProductCardMetadata(item.target);

			if (card === undefined) {
				return;
			}

			let type = MLItemType.PRODUCT;
			if (card.isPinnedProduct) {
				type = MLItemType.PINNED_PRODUCT;
			} else if (card.type === 'sponsored-product') {
				type = MLItemType.SPONSORED_PRODUCT;
				if (this.featureFlags().isUsingCriteoSPAAdServer && !this.gridViewBeaconFired) {
					this.criteoService.fireBeacon(this.onViewGridItemBeacon);
					this.gridViewBeaconFired = true;
				}
			} else if (card.type === 'pinned-content') {
				type = MLItemType.PINNED_CONTENT_SPOT;
			}

			const ecode: string = this.productInfoList[card.index];
			if (this.ecodesAlreadyReported.includes(ecode)) {
				continue;
			}
			this.ecodesAlreadyReported.push(ecode);

			const nextItem: MLItem = {
				id: ecode,
				num: card.index + 1, // 1-based index
				type
			};
			itemGroup.push(nextItem);

			// Once the intersection event has fired on an item, stop tracking its intersections.
			this.productCardObserver.unobserve(item.target);
		}

		this.plpMlService.sendImpressionEventData(
			itemGroup,
			this.currentFacets,
			this.chainAbbr,
			this.productListData?.facetVOs ?? [],
			this.productListData?.totalCount ?? 0
		);
	}

	/**
	 * Determines and returns the state of facets (open or closed) based on the provided facets array.
	 * The first 4 facets are open by default, while the rest are closed. If it's plp3 template, only the first facet, category, will be open.
	 *
	 * @param facets - An array of `FacetVO` objects representing the facets to be processed.
	 * @returns A record where each key is a facet attribute name and the value is an object
	 *          containing the `open` and `showMore` states.
	 */
	private getOpenFacets(facets: FacetVO[]): Record<string, { open: boolean; showMore: boolean }> {
		if (this.openFacets !== null) return this.openFacets;

		const openFacets = {};

		facets.forEach((facet, index) => {
			let numberOfOpenFacets = 3;

			// Number of open facets for superfamily and plp3 template
			if (this.hasCatBubbles && this.productListSEOData !== null) {
				numberOfOpenFacets = this.isPlp3Template ? 0 : 4;
			}

			if (index <= numberOfOpenFacets) {
				openFacets[facet.attrName] = { open: true, showMore: false };
			} else {
				openFacets[facet.attrName] = { open: false, showMore: false };
			}
		});

		return openFacets;
	}

	private setNikeConnectedProducts(displayableProducts) {
		let isNikeConnected = false;
		displayableProducts.forEach((product) => {
			if (product.attributes) {
				isNikeConnected = !!JSON.parse(product.attributes).find(
					(attribute) => attribute['5632'] && attribute['5632'] === 'Nike APE Exclusive'
				);
			}

			if (isNikeConnected && product.parentPartnumber) {
				this.nikeConnectedProducts.push(product.parentPartnumber);
				isNikeConnected = false;
			}
		});
	}

	onFacetToggled({ attrName, open, showMore }: FacetAccordionElement) {
		this.openFacets[attrName] = { open, showMore };
	}

	setCurrentFilterIndex(index: number): void {
		this.currentFilterIndex.set(index);
	}

	focusFirstAccordion(): void {
		setTimeout(() => {
			this.currentFilterIndex.set(0);
			this.accordionFocusHandler(this.currentFilterIndex());
		}, 100);
	}

	arrowDownKeyHandler(event: KeyboardEvent): void {
		const facetVosLength = this.productListData?.facetVOs?.length; //number of accordion components in filter column

		if (!facetVosLength) {
			return;
		}

		event.preventDefault();
		const arrowDownFilterIndex =
			this.currentFilterIndex() === null
				? 0
				: (this.currentFilterIndex() + 1) % facetVosLength;

		this.currentFilterIndex.set(arrowDownFilterIndex);
		this.accordionFocusHandler(this.currentFilterIndex());
	}

	arrowUpKeyHandler(event: KeyboardEvent): void {
		const facetVosLength = this.productListData?.facetVOs?.length; //number of accordion components in filter column

		if (!facetVosLength) {
			return;
		}

		event.preventDefault();
		let arrowUpFilterIndex =
			this.currentFilterIndex() === null
				? this.currentFilterIndex() - 1
				: (this.currentFilterIndex() - 1 + facetVosLength) % facetVosLength;

		// Navigating through the checkboxes, a category item, size option, brand search input or the "view more" link does not change the accordion we are in.
		// This makes sure we return to the current accordion header, and not the skipping one above.
		// if for some reason your are going to change classes in filter.component.html make sure to update this
		const filterOptionsClasses = [
			'filter-value-count-container',
			'homefield-checkbox-input',
			'homefield-text-link',
			'homefield-actiontext',
			'size-filter'
		];

		const wasKeyPressedInAFilterOption = filterOptionsClasses.some((className) =>
			(event.target as HTMLElement).classList?.contains(className)
		);
		const wasKeyPressedFromBrandSearchInput = (event.target as HTMLElement)
			.getAttribute('id')
			?.includes('homefield-textinput');

		if (wasKeyPressedInAFilterOption || wasKeyPressedFromBrandSearchInput) {
			arrowUpFilterIndex = this.currentFilterIndex();
		}

		this.currentFilterIndex.set(arrowUpFilterIndex);
		this.accordionFocusHandler(this.currentFilterIndex());
	}

	tabKeyHandler(index: number): void {
		this.currentFilterIndex.set(index);
	}

	private accordionFocusHandler(facetIndex: number): void {
		const accordionHeaders =
			this.deviceBreakPoint <= 3
				? document.querySelectorAll('.filter-container .accordion-header')
				: document.querySelectorAll('.plp-srlp-filters .accordion-header');
		if (accordionHeaders[facetIndex]) {
			(accordionHeaders[facetIndex] as HTMLElement).focus();
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected trackByFn(index: number, _item: any): number {
		return index;
	}

	protected trackFilterName(index: number, facetVO: FacetVO): string {
		return index.toString() + facetVO.attrName;
	}

	protected trackDPLD(index: number, item: any): string {
		// Optional combo of properties from each type of card
		// Normal = name + parentPartnumber, PiQ = product_id, Pinned Content = headline
		let value = index.toString();
		if (item.name) {
			value += `-${item.name}-${item?.parentPartnumber}`;
		}
		if (item.product_id) {
			value += `-${item.product_id}`;
		}
		if (item.headline) {
			value += `-${item.headline}`;
		}
		return value;
	}

	onSkipLinkEnter(currentTarget: KeyboardEvent) {
		// Prevent the click event from being triggered on the "homefield-color-swatch" component when the button is focused.
		currentTarget.preventDefault();

		this.skipLinkOpen = false;
		const productCardContent: HTMLDivElement | null = document.querySelector(
			'#plp-container .product-card .swatches-container button'
		);
		if (productCardContent) {
			productCardContent.focus();
		}
	}
}
